import { useEffect } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { IS_LOCALHOST, IS_PLG_PROD } from 'constants/Global.constants';

import { AccountType } from 'api/resources/accounts/types';
import useAccount from 'hooks/useAccount';
import { useCurrentUserTags } from 'hooks/useCurrentUserTags';
import { isPolyEmail } from 'hooks/useProjectResources';
import { updatePendo } from 'lib/pendo';
import { useTrackingConsent } from './useTrackingConsent';

interface UsePendoOptions {
  projectId?: string;
  accountId?: string;
  additionalInfo?: Record<string, any>;
}

export const usePendo = ({
  projectId,
  accountId,
  additionalInfo,
}: UsePendoOptions = {}) => {
  const { user } = useAuth0();
  const { account } = useAccount();
  const { hasStatisticsConsent, isConsentReady } = useTrackingConsent();
  // Switched to environmental variable check for PLG prod since the feature flag
  // check breaks access to public pages when the user hasn't logged in, which the
  // OT integration flow relies on.
  const isPlgProd = IS_PLG_PROD;

  const userTags = useCurrentUserTags({ accountId, isEnabled: !!accountId });
  const accountName = account?.name ?? '';
  const accountType =
    account?.conversation_limits.account_type ?? AccountType.STANDARD;

  const pendoKey = process.env.NEXT_PUBLIC_PENDO_APP_ID;

  useEffect(() => {
    // Don't initialize Pendo if user hasn't consented to statistics cookies
    if (!pendoKey || !isConsentReady || !hasStatisticsConsent || IS_LOCALHOST) {
      return;
    }

    // Generate visitor ID: use email if available, otherwise create anonymous ID
    const getVisitorId = () => {
      if (user?.email) {
        return user.email;
      }

      // For anonymous users, generate or retrieve a persistent ID from sessionStorage
      const anonymousIdKey = 'pendo-anonymous-visitor-id';
      let anonymousId = sessionStorage.getItem(anonymousIdKey);

      if (!anonymousId) {
        anonymousId = `anonymous-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
        sessionStorage.setItem(anonymousIdKey, anonymousId);
      }

      return anonymousId;
    };

    const visitorId = getVisitorId();

    const visitor = {
      id: visitorId,
      environment: process.env.NEXT_PUBLIC_POLYAI_ENVIRONMENT || '',
      region: process.env.NEXT_PUBLIC_POLYAI_REGION || '',
      userType:
        user?.email && isPolyEmail(user.email) ? 'internal' : 'external',
      isPlg: isPlgProd,
      ...(user?.email && { email: user.email }), // Add email as metadata when available
      ...(projectId && { project_id: projectId }),
      account_type: accountType,
      ...(userTags && userTags.length > 0 && { tags: userTags }),
      ...(additionalInfo && { ...additionalInfo }),
    };

    const pendoAccount = accountId
      ? { id: accountId, name: accountName }
      : { id: 'before-sign-in' };

    updatePendo(pendoKey, visitor, pendoAccount);
  }, [
    pendoKey,
    user?.email,
    projectId,
    accountId,
    accountName,
    accountType,
    userTags,
    hasStatisticsConsent,
    isConsentReady,
    isPlgProd,
    additionalInfo,
  ]);
};
